import { Button } from '@components/common';
import { CornerDownLeft, Eye, Pencil, Send, Trash2 } from 'lucide-react';
import React from 'react';
import { JournalEntryRecord } from '../../../../types/accounting';
import { JournalEntryStatusBadge } from './JournalEntryStatusBadge';
import {
  formatPostingScopeLabel,
  formatSourceModuleLabel,
} from './journalForm';

interface JournalEntriesTableProps {
  entries: JournalEntryRecord[];
  outletName?: string | null;
  isFetching?: boolean;
  onView: (entry: JournalEntryRecord) => void;
  onEdit: (entry: JournalEntryRecord) => void;
  onDelete: (entry: JournalEntryRecord) => void;
  onPost: (entry: JournalEntryRecord) => void;
  onReverse: (entry: JournalEntryRecord) => void;
}

export function JournalEntriesTable({
  entries,
  outletName,
  isFetching = false,
  onView,
  onEdit,
  onDelete,
  onPost,
  onReverse,
}: JournalEntriesTableProps) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {isFetching ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      ) : null}
      <div className="max-h-[32rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Date
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Memo
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Scope
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Source
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id} className="border-t border-gray-100">
                <td className="px-5 py-4 font-medium text-gray-900">
                  {entry.entry_date}
                </td>
                <td className="px-5 py-4 text-gray-700">
                  <div className="font-medium text-gray-900">
                    {entry.display_memo || entry.memo || 'Untitled journal'}
                  </div>
                  {entry.related_bill_number ? (
                    <div className="text-xs text-gray-500">
                      Bill #{entry.related_bill_number}
                    </div>
                  ) : null}
                </td>
                <td className="px-5 py-4 text-gray-600">
                  {formatPostingScopeLabel(entry.posting_scope, outletName)}
                </td>
                <td className="px-5 py-4 text-gray-600">
                  {formatSourceModuleLabel(entry.source_module)}
                </td>
                <td className="px-5 py-4">
                  <JournalEntryStatusBadge status={entry.status} />
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() => onView(entry)}
                    >
                      <Eye size={14} />
                      View
                    </Button>
                    {entry.status === 'draft' ? (
                      <>
                        <Button
                          variant="text"
                          size="sm"
                          onClick={() => onEdit(entry)}
                        >
                          <Pencil size={14} />
                          Edit
                        </Button>
                        <Button
                          variant="text"
                          size="sm"
                          onClick={() => onPost(entry)}
                        >
                          <Send size={14} />
                          Post
                        </Button>
                        <Button
                          variant="text"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => onDelete(entry)}
                        >
                          <Trash2 size={14} />
                          Delete
                        </Button>
                      </>
                    ) : entry.status === 'posted' ? (
                      <Button
                        variant="text"
                        size="sm"
                        onClick={() => onReverse(entry)}
                      >
                        <CornerDownLeft size={14} />
                        Reverse
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400">
                        No actions available
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
